import { action, observable, makeObservable } from 'mobx';
import axios from 'axios';

import { adminRequestStatus, DEFAULT_DATA_LIMIT } from '../utils/constants';
import { getClientIconByUserData, getMessageKeyForAdminRequestStatus, capitalizeFirstLetter } from '../utils/helpers';
import {
  getAssignedAsAdminSuccessMessage,
  getFullCompanyAddress,
  getMessageUserSuccessfullyRemoved,
  getMessageUserSuccessfullyRemovedAsCompanyAdmin,
} from '../pages/CompanyAdmin/utils/string-transformation';
import { errorAlert, successAlert } from '../utils/alerts';
import { BRANCH_NOT_FOUND } from '../pages/ProfilePage/constants';

const { REACT_APP_API_URL } = process.env;

class CompanyStore {
  constructor(profileStore) {
    makeObservable(this, {
      isLoadingAdminRequest: observable,
      requestStatusMessageKey: observable,
      userInfoList: observable,
      adminUsersPageLoading: observable,
      offsetAllUsers: observable,
      totalUsersInDatabase: observable,
      companyInfo: observable,
      pageLoading: observable,
      lastFourAdmins: observable,
      totalAdminsInCompany: observable,
      totalUsersInCompany: observable,
      dispoSuiteProjects: observable,
      requestCompanyAdmin: action,
      getAdminRequestStatus: action,
      getAllUsers: action,
      assignAsCompanyAdmin: action,
      removeUserProfile: action,
      removeCompanyAdmin: action,
      setPageLoading: action.bound,
      getCompanyDetails: action,
      resetCompanyStoreFields: action
    });

    this.profileStore = profileStore;
  }

  isLoadingAdminRequest = false;

  requestStatusMessageKey = '';

  userInfoList = [];

  adminUsersPageLoading = false;

  offsetAllUsers = 0;

  totalUsersInDatabase = 0;

  companyInfo = null;

  pageLoading = false;

  lastFourAdmins = [];

  totalAdminsInCompany = 0;

  totalUsersInCompany = 0;

  dispoSuiteProjects = -1;

  async requestCompanyAdmin(t) {
    this.isLoadingAdminRequest = true;
    try {
      const authToken = await this.profileStore.getAuthToken();
      await axios
        .post(
          `${REACT_APP_API_URL}/companies/${this.profileStore.company.id}/admin-request`,
          {},
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        )
        .then(() => (this.isLoadingAdminRequest = false));

      this.requestStatusMessageKey = getMessageKeyForAdminRequestStatus(adminRequestStatus.OPEN);
      successAlert(t('companyInfo.adminRequestSuccess'));
    } catch (error) {
      errorAlert(t('companyInfo.adminRequestErrorMessage'));
    }
  }

  async getAdminRequestStatus() {
    const authToken = await this.profileStore.getAuthToken();
    const companyId = this.profileStore.company ? this.profileStore.company.id : null;
    try {
      const {
        data: { status },
      } = await axios.get(`${REACT_APP_API_URL}/companies/${companyId}/admin-request`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      this.requestStatusMessageKey = getMessageKeyForAdminRequestStatus(status);
    } catch (error) {
      //errorAlert(error);
    }
  }

  async getAllUsers(searchTerm = '') {
    const authToken = await this.profileStore.getAuthToken();

    try {
      const { data } = await axios.get(
        `${REACT_APP_API_URL}/users?limit=${DEFAULT_DATA_LIMIT}&search=${searchTerm}&offset=${this.offsetAllUsers}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const mapUSerList = data.rows.map(userRow => {
        const { Companies, Branches } = userRow;
        const { CompanyUser } = Companies[0];
        const userCompany = Companies[0];
        const userBranch = Branches[0];
        const branchAddress =
          userBranch?.name === BRANCH_NOT_FOUND
            ? BRANCH_NOT_FOUND
            : userBranch
            ? getFullCompanyAddress(userBranch.street, userBranch.zip, userBranch.city)
            : null;
        return {
          id: userRow.id,
          fullName: [userRow.firstName, userRow.lastName].join(' '),
          firstName: userRow.firstName,
          lastName: userRow.lastName,
          email: userRow.email,
          address: getFullCompanyAddress(userCompany.street, userCompany.zip, userCompany.city),
          userFunction: userRow.function,
          isAdmin: CompanyUser.isOwner,
          companyName: userCompany.name,
          pictureSrc: userRow.picture,
          branchAddress,
          clientIconUrl: getClientIconByUserData(userRow.application),
          clientIconName: capitalizeFirstLetter(userRow.application),
        };
      });

      this.totalUsersInDatabase = data.count;
      this.userInfoList = [...this.userInfoList, ...mapUSerList];
      this.offsetAllUsers += DEFAULT_DATA_LIMIT;
    } catch (err) {
      this.companyUsersError = true;
    }
  }

  async getAllUsersBySearchTerm(searchTerm) {
    this.userInfoList = [];
    this.offsetAllUsers = 0;
    this.totalUsersInDatabase = 0;
    this.companyUsersError = false;
    await this.getAllUsers(searchTerm);
  }

  updaterAssignCompanyAdmin(assigneeId) {
    this.userInfoList = this.userInfoList.map(userInfo => {
      if (userInfo.id === assigneeId) {
        userInfo.isAdmin = true;
      }
      return userInfo;
    });
  }

  async assignAsCompanyAdmin(assigneeId, userFullName, companyName, t) {
    const authToken = await this.profileStore.getAuthToken();
    try {
      await axios.post(
        `${REACT_APP_API_URL}/users/admin`,
        { assignee: assigneeId },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const successMessage = getAssignedAsAdminSuccessMessage(
        t('companyAdminUsers.successMessage'),
        userFullName,
        companyName
      );
      this.updaterAssignCompanyAdmin(assigneeId);
      successAlert(successMessage);
    } catch (err) {
      errorAlert(t('notifications.genericError'));
    }
  }

  updaterRemoveCompanyUser(userId) {
    this.userInfoList = this.userInfoList.filter(userInfo => userInfo.id !== userId);
    this.totalUsersInDatabase--;
  }

  async removeUserProfile(userId, userFullName, companyName, t) {
    const authToken = await this.profileStore.getAuthToken();
    try {
      await axios.post(
        ` ${REACT_APP_API_URL}/users/remove`,
        { userId },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const successfullyRemovedMessage = getMessageUserSuccessfullyRemoved(
        t('companyAdminUsers.successfullyRemovedMessage'),
        userFullName,
        companyName
      );

      this.updaterRemoveCompanyUser(userId);
      successAlert(successfullyRemovedMessage);
    } catch (error) {
      errorAlert(t('notifications.genericError'));
    }
  }

  updateRemoveCompanyAdmin(userId) {
    this.userInfoList = this.userInfoList.map(userInfo => {
      if (userInfo.id === userId) {
        userInfo.isAdmin = false;
      }
      return userInfo;
    });
  }

  async removeCompanyAdmin(userId, userFullName, companyName, t) {
    const authToken = await this.profileStore.getAuthToken();
    try {
      await axios.post(
        `${REACT_APP_API_URL}/users/remove-company-admin-role`,
        { userId },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const successfullyRemovedAdminMessage = getMessageUserSuccessfullyRemovedAsCompanyAdmin(
        t('companyAdminUsers.successfullyRemovedAdminMessage'),
        userFullName,
        companyName
      );

      this.updateRemoveCompanyAdmin(userId, this.userInfoList);
      successAlert(successfullyRemovedAdminMessage);
    } catch (err) {
      errorAlert(t('notifications.genericError'));
    }
  }

  setAdminUsersPageLoading(loadingState) {
    this.adminUsersPageLoading = loadingState;
  }

  setPageLoading(loadingState) {
    this.pageLoading = loadingState;
  }

  getCompanyDetails() {
    const authToken = this.profileStore.authToken;
    axios
      .get(`${REACT_APP_API_URL}/companies/${this.profileStore.company.id}/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(({ data }) => {
        const { company } = data;
        this.companyInfo = {
          description: company.description,
          address: getFullCompanyAddress(company.street, company.zip, company.city),
          phoneNumber: company.phone,
          companyUrl: company.url,
          name: company.name,
        };

        this.lastFourAdmins = data.lastFourJoin.map(user => {
          const branchAddress =
            user.Branches[0]?.name === BRANCH_NOT_FOUND
              ? BRANCH_NOT_FOUND
              : user.Branches.length > 0
              ? getFullCompanyAddress(user.Branches[0].street, user.Branches[0].zip, user.Branches[0].city)
              : null;
          return {
            id: user.id,
            fullName: [user.firstName, user.lastName].join(' '),
            firstName: user.firstName,
            lastName: user.lastName,
            address: this.companyInfo.address,
            email: user.email,
            userFunction: user.function,
            isAdmin: user.isOwner,
            pictureSrc: user.picture,
            branchAddress,
            clientIconUrl: getClientIconByUserData(user.application),
            clientIconName: capitalizeFirstLetter(user.application),
          };
        });

        this.totalUsersInCompany = data.totalUsersInCompany;
        this.dispoSuiteProjects = data.dispoSuite.projects;
        this.totalAdminsInCompany = data.admins;
        this.setPageLoading(false);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  resetCompanyStoreFields() {
    this.offsetAllUsers = 0;
    this.userInfoList = [];
  }
}

export default CompanyStore;
